import React, { FormEvent, FunctionComponent, useState, useRef, ReactNode } from 'react'
import {
  Button,
  Grid,
  Input,
  InputBirthday,
  InputBirthdayValidity,
  InputOnFocus,
  InputPhone,
  Text,
  useIsDesignLanguage,
  DesignLanguage,
  useDesignLanguagePerClass,
} from '@afterpaytouch/core'
import { AppLink, Form, ProfileCard } from '..'
import { useCheckoutSizes } from '../../utils/responsive'
import {
  useCheckoutRegistrationAddress,
  useConsumerSelector,
  useCountry,
  useMobileLogin,
  useResetPassword,
  useSecurity2faFlow,
  useTwoFactorChallengeSelector,
  useVerifyChallengeFlow,
} from '../../state'
import { PasswordResetTwoFactorChallenge, SupportedLocale, TwoFactorChallengeRequest, TwoFactorChallengeType, VerificationErrorCode } from '../../model'
import { useRouter } from 'next/router'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { Route } from '../../config/router'
import { maskPhoneNumber } from '../../utils/phone-numbers'
import { MAX_AGE_RESTRICTION, MINIMUM_AGE_RESTRICTION } from '../CreateAccount/constants'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { isCallable } from '@afterpay/utils'
import styles from './style.module.css'
import clsx from 'clsx'
import { useTranslation } from '../../utils/convergence'

interface Props {
  onSuccess: () => void
  onError: (isMobileLoginVerification: boolean) => void
  isMobileLoginVerification?: boolean
}

interface VerifyChallengeTouched {
  [x: string]: boolean
}

interface VerifyChallengeErrors {
  [x: string]: boolean
}

interface ChallengesForm {
  PHONE_NUMBER?: string
  DATE_OF_BIRTH?: string
  CREDIT_CARD?: string
  LAST_NAME?: string
}

const getFormFields = (mandatoryFields: TwoFactorChallengeType[]): ChallengesForm => {
  const initialValue = {}
  return mandatoryFields.reduce((object, challengeType) => {
    object[challengeType] = ''
    return object
  }, initialValue)
}

export const VerifyChallenge: FunctionComponent<Props> = ({ onSuccess, onError, isMobileLoginVerification = false }) => {
  const { t } = useTranslation()
  const { textSize } = useCheckoutSizes()
  const locale = useRouter().locale as SupportedLocale
  const country = useCountry()
  const { email } = useConsumerSelector()
  const { code } = useResetPassword()
  const twoFactorChallenges: PasswordResetTwoFactorChallenge[] = useTwoFactorChallengeSelector()
  const initialAddresses = useCheckoutRegistrationAddress()
  const mandatoryFields: TwoFactorChallengeType[] = twoFactorChallenges.map((challenge) => challenge.type)
  const formFields = getFormFields(mandatoryFields)
  const [formData, setFormData] = useState<ChallengesForm>(formFields)
  const [touched, setTouched] = useState<VerifyChallengeTouched>({})
  const [errors, setErrors] = useState<VerifyChallengeErrors>({})
  const [apiErrorMessage, setApiErrorMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const { checkChallenge } = useVerifyChallengeFlow()
  const { checkDob } = useSecurity2faFlow()
  const [dobErrorText, setDobErrorText] = useState('')
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const buttonClickCountRef = useRef<number>(0)
  const { isMobileLogin } = useMobileLogin()
  const countryMinimumAgeRestriction = MINIMUM_AGE_RESTRICTION[country] ?? MINIMUM_AGE_RESTRICTION.DEFAULT_COUNTRY
  const minAge = countryMinimumAgeRestriction[initialAddresses[0]?.state] ?? countryMinimumAgeRestriction.DEFAULT

  const handleInputChange = (field: string, value: string): void => {
    setFormData({
      ...formData,
      [field]: value,
    })
    setTouched({
      ...touched,
      [field]: true,
    })

    const { [field]: inputName, ...otherErrors } = errors

    setErrors({
      ...otherErrors,
      [field]: false,
    })

    setApiErrorMessage('')
  }

  const handleDobBlur = (e: InputOnFocus, dob: string, validity: InputBirthdayValidity): void => {
    const name: TwoFactorChallengeType = TwoFactorChallengeType.DATE_OF_BIRTH
    const { dateOfBirth, ...otherErrors } = errors
    let errorText: string

    switch (validity) {
      case InputBirthdayValidity.Valid:
        errorText = ''
        break
      case InputBirthdayValidity.OverAge:
        errorText = t('verifyChallenge:error.overAge', { maxAge: MAX_AGE_RESTRICTION })
        break
      case InputBirthdayValidity.UnderAge:
        errorText = t('verifyChallenge:error.underAge', { minAge })
        break
      default:
        errorText = t('verifyChallenge:error.invalidDOB')
        break
    }

    setDobErrorText(errorText)
    if (isMobileLogin && validity !== InputBirthdayValidity.Valid) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: validity })
    }

    setTouched({
      ...touched,
      [name]: true,
    })
    setErrors({
      ...otherErrors,
      [name]: validity !== InputBirthdayValidity.Valid,
    })
  }

  const handleLNameBlur = (e: InputOnFocus): void => {
    const name: TwoFactorChallengeType = TwoFactorChallengeType.LAST_NAME
    const { [name]: removedError, ...otherErrors } = errors
    const { value } = e.target
    const valid: boolean = !isUndefinedOrEmpty(value)
    setTouched({
      ...touched,
      [name]: true,
    })
    setErrors({
      ...otherErrors,
      ...(!valid && touched[name] && { [name]: true }),
    })
  }

  const handleCardBlur = (e: InputOnFocus): void => {
    const name: TwoFactorChallengeType = TwoFactorChallengeType.CREDIT_CARD
    const { value } = e.target
    const { [name]: cardError, ...otherErrors } = errors
    const inValid = isUndefinedOrEmpty(value) || value.length !== 7
    setTouched({
      ...touched,
      [name]: true,
    })
    setErrors({
      ...otherErrors,
      [name]: inValid,
    })
  }

  const handleMobileBlur = (e: InputOnFocus, rawValue: string, validity: boolean): void => {
    const name: TwoFactorChallengeType = TwoFactorChallengeType.PHONE_NUMBER
    const { [name]: removedError, ...otherErrors } = errors
    const validLength: boolean = !isUndefinedOrEmpty(rawValue)
    const valid = validLength && validity
    setTouched({
      ...touched,
      [name]: true,
    })
    setErrors({
      ...otherErrors,
      ...(!valid && touched[name] && { [name]: true }),
    })
  }

  const doCheckChallenge = async (): Promise<void> => {
    try {
      setApiErrorMessage('')
      if (isMobileLoginVerification && typeof formData.DATE_OF_BIRTH !== 'undefined' && typeof formData.LAST_NAME !== 'undefined') {
        isCallable(checkDob) && (await checkDob(new Date(formData.DATE_OF_BIRTH), formData.LAST_NAME))
      } else {
        const requestEmail = email ?? ''
        const resetCode = code ?? ''

        const request: TwoFactorChallengeRequest = {
          email: requestEmail,
          code: parseInt(resetCode),
          twoFactorChallenges: { ...formData },
          countryCode: country,
        }
        await checkChallenge(request.email, request.code, request.twoFactorChallenges, request.countryCode)
      }
      onSuccess()
    } catch (e) {
      const { code: errorCode } = e
      const error = t('verifyChallenge:error.recordMismatch')
      if (errorCode === VerificationErrorCode.PasswordResetTwoFactorChallengesInvalid || errorCode === VerificationErrorCode.InvalidDob) {
        setApiErrorMessage(error)
        setSubmitted(false)
      } else {
        onError(isMobileLoginVerification)
      }
    }
  }

  const isFormComplete = (): boolean =>
    Object.keys(formData)
      .filter((challenge: TwoFactorChallengeType) => mandatoryFields.includes(challenge))
      .every((current) => !isUndefinedOrEmpty(formData[current]))

  const isFormValid = (): boolean =>
    Object.keys(errors)
      .filter((d: TwoFactorChallengeType) => mandatoryFields.includes(d))
      .every((current) => !errors[current])

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setSubmitted(true)

    if (isMobileLogin) {
      logEvent(TrackingEvent.PRESSED_DOB_LAST_NAME_VERIFY_PAGE_CONTINUE, { attempt: ++buttonClickCountRef.current })
    }

    if (isFormComplete() && isFormValid()) {
      await doCheckChallenge()
    } else {
      setSubmitted(false)
    }
  }
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const containerClass = clsx(styles.verifyChallengeContainer, designLanguageStyles.verifyChallengeContainer)

  return (
    <div className={containerClass}>
      <Form onSubmit={handleSubmit}>
        {isMobileLoginVerification && <ProfileCard allowLogout />}
        <Grid>
          <Text color={isArcadeDesignLanguage ? 'Subtle' : undefined} size={textSize}>
            {t('verifyChallenge:description')}
          </Text>
        </Grid>
        {twoFactorChallenges.map((challenge: PasswordResetTwoFactorChallenge): ReactNode => {
          switch (challenge.type) {
            case TwoFactorChallengeType.PHONE_NUMBER:
              return (
                <Grid key={challenge.hint}>
                  <MaskedElement stylingModifier='wrap' actionName='Verify Challenge Mobile Input'>
                    <InputPhone
                      allowedCountries={[
                        {
                          countryCode: country,
                          name: t('common:countryName', { context: country }),
                        },
                      ]}
                      label={t('verifyChallenge:mobileInputLabel', { hint: maskPhoneNumber(challenge.hint) })}
                      value={formData?.PHONE_NUMBER}
                      size={'Large'}
                      testNameSpace='verify-challenge-mobile'
                      onChange={(_, rawValue) => handleInputChange(TwoFactorChallengeType.PHONE_NUMBER, rawValue)}
                      onBlur={handleMobileBlur}
                      error={errors.PHONE_NUMBER}
                      errorText={errors.PHONE_NUMBER && t('verifyChallenge:error.invalidMobile')}
                    />
                  </MaskedElement>
                </Grid>
              )
            case TwoFactorChallengeType.CREDIT_CARD:
              return (
                <Grid key={challenge.hint}>
                  <MaskedElement stylingModifier='wrap' actionName='Verify Challenge Card Input'>
                    <Input
                      format='0000 00'
                      label={t('verifyChallenge:cardChallenge', { hint: challenge.hint })}
                      size={'Large'}
                      name={TwoFactorChallengeType.CREDIT_CARD}
                      value={formData?.CREDIT_CARD}
                      onChange={(_, rawValue) => handleInputChange(TwoFactorChallengeType.CREDIT_CARD, rawValue)}
                      onBlur={handleCardBlur}
                      error={errors.CREDIT_CARD}
                      errorText={errors.CREDIT_CARD && t('verifyChallenge:error.invalidCardNumber')}
                    />
                  </MaskedElement>
                </Grid>
              )
            case TwoFactorChallengeType.DATE_OF_BIRTH:
              return (
                <Grid key={TwoFactorChallengeType.DATE_OF_BIRTH}>
                  <MaskedElement stylingModifier='wrap' actionName='Verify Challenge DOB Input'>
                    <InputBirthday
                      error={errors.DATE_OF_BIRTH}
                      errorText={dobErrorText}
                      hasDateFormatLabel={!isArcadeDesignLanguage}
                      label={t('verifyChallenge:dateOfBirth')}
                      locale={locale}
                      name={TwoFactorChallengeType.DATE_OF_BIRTH}
                      onChange={(_, dateOfBirth) => handleInputChange(TwoFactorChallengeType.DATE_OF_BIRTH, dateOfBirth)}
                      onBlur={handleDobBlur}
                      testNameSpace='verify-challenge-dob'
                      size={'Large'}
                    />
                  </MaskedElement>
                </Grid>
              )
            case TwoFactorChallengeType.LAST_NAME:
              return (
                <Grid key={challenge.hint}>
                  <MaskedElement stylingModifier='wrap' actionName='Verify Challenge Last Name Input'>
                    <Input
                      label={t('verifyChallenge:lastName', { hint: challenge.hint })}
                      size={'Large'}
                      name={TwoFactorChallengeType.LAST_NAME}
                      value={formData?.LAST_NAME}
                      onChange={(_, rawValue) => handleInputChange(TwoFactorChallengeType.LAST_NAME, rawValue)}
                      onBlur={handleLNameBlur}
                      testNameSpace='last-name'
                      error={errors.LAST_NAME}
                      placeholder={isArcadeDesignLanguage ? t('verifyChallenge:lastNamePlaceholder') : undefined}
                      errorText={errors.LAST_NAME && t('verifyChallenge:error.invalidLastName')}
                    />
                  </MaskedElement>
                </Grid>
              )
            default:
              return null
          }
        })}
        {apiErrorMessage !== '' && (
          <Grid>
            <Text size='S' color='Fire' aria-live='polite'>
              {apiErrorMessage}
            </Text>
          </Grid>
        )}
        <Grid>
          <Grid.Item>
            <Button disabled={submitted || !isFormComplete() || !isFormValid()} size={'Large'} testNameSpace='verify-challenge' type='submit' padding='Fluid'>
              {t('common:continue')}
            </Button>
          </Grid.Item>
        </Grid>
        <Grid>
          {!isMobileLoginVerification && (
            <AppLink underline center noMargin route={Route.VERIFY}>
              <Text size={textSize} bold>
                {t('verifyChallenge:smsAnchor')}
              </Text>
            </AppLink>
          )}
        </Grid>
      </Form>
    </div>
  )
}
